import React, { useState, useEffect } from 'react';
import { Card, CardContent, Typography, Box, CircularProgress, IconButton } from '@mui/material';
import { People as PeopleIcon, ChevronLeft, ChevronRight } from '@mui/icons-material';
import { getRotaOptionById } from '../constants/rotaOptions';
import axiosInstance from '../utils/axiosConfig';

interface Personnel {
  _id: string;
  name: string;
  email: string;
  roles: string[];
  profileImage?: string;
}

interface RotaEntry {
  _id?: string;
  personnelId: string | Personnel;
  date: string;
  status: string;
  notes?: string;
  startTime?: string; // Format: HH:mm
  endTime?: string; // Format: HH:mm
  createdBy?: string;
  updatedBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface RotaWidgetProps {
  initialDate?: Date;
}

const formatNameList = (names: string[]): string => {
  if (names.length === 0) return '';
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  
  const lastNames = names.slice(-2);
  const firstNames = names.slice(0, -2);
  
  return `${firstNames.join(', ')}, ${lastNames[0]} and ${lastNames[1]}`;
};

const formatTime = (time: string): string => {
  const [hours, minutes] = time.split(':');
  const hour = parseInt(hours);
  const suffix = hour >= 12 ? 'pm' : 'am';
  const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
  return minutes === '00' ? `${displayHour}${suffix}` : `${displayHour}:${minutes}${suffix}`;
};

const generateConversationalSummary = (
  rotaEntries: RotaEntry[], 
  personnel: Personnel[], 
  performances: any[],
  targetDate: Date = new Date()
): string => {
  const dateStr = targetDate.toISOString().split('T')[0];
  const todaysEntries = rotaEntries.filter(entry => 
    entry.date === dateStr || 
    new Date(entry.date).toDateString() === targetDate.toDateString()
  );

  console.log('Debug - Today\'s entries:', todaysEntries);
  console.log('Debug - Personnel:', personnel);

  if (todaysEntries.length === 0 && performances.length === 0) {
    return "No staff are scheduled to work today.";
  }

  // Check if it's Tuesday (staff meeting day)
  const isTuesday = targetDate.getDay() === 2;

  // Group entries by person to handle multiple shifts per person
  const entriesByPerson = new Map<string, RotaEntry[]>();
  
  todaysEntries.forEach(entry => {
    if (!entry.personnelId) return;
    const personId = typeof entry.personnelId === 'string' ? entry.personnelId : entry.personnelId._id;
    if (!entriesByPerson.has(personId)) {
      entriesByPerson.set(personId, []);
    }
    entriesByPerson.get(personId)!.push(entry);
  });

  const parts: string[] = [];
  
  // Count total working staff (unique people) - exclude unavailable and not-working from working count
  const workingStatuses = ['working', 'programming', 'event', 'am', 'pm', 'abroad', 'traveling', 'cooler', 'meeting'];
  const workingPeople = new Set();
  entriesByPerson.forEach((entries, personId) => {
    if (entries.some(e => workingStatuses.includes(e.status))) {
      workingPeople.add(personId);
    }
  });
  const workingCount = workingPeople.size;
  
  if (workingCount > 0) {
    parts.push(`Today there ${workingCount === 1 ? 'is' : 'are'} ${workingCount} staff working.`);
  }

  // Add Tuesday context if applicable
  if (isTuesday && workingCount > 0) {
    parts.push(`Today is typically a staff meeting day.`);
  }

  // Analyze each person's schedule
  const fullDayWorkers: string[] = [];
  const morningWorkers: string[] = [];
  const afternoonWorkers: string[] = [];
  const eventWorkers: string[] = [];
  const coolerWorkers: { name: string; timing: string }[] = [];
  const abroadWorkers: string[] = [];
  const travelingWorkers: string[] = [];
  const meetingWorkers: { name: string; count: number; timing: string[] }[] = [];

  entriesByPerson.forEach((entries, personId) => {
    const person = personnel.find(p => p._id === personId);
    if (!person) return;
    
    const firstName = person.name.split(' ')[0];
    const statuses = entries.map(e => e.status);
    
    // Check for full day work (working or programming)
    if (statuses.includes('working') || statuses.includes('programming')) {
      fullDayWorkers.push(firstName);
    }
    
    // Check for part-time shifts
    if (statuses.includes('am')) {
      morningWorkers.push(firstName);
    }
    
    if (statuses.includes('pm')) {
      afternoonWorkers.push(firstName);
    }
    
    // Check for events
    if (statuses.includes('event')) {
      eventWorkers.push(firstName);
    }
    
    // Check for cooler work with timing
    if (statuses.includes('cooler')) {
      // Find the cooler entry to check timing
      const coolerEntry = entries.find(e => e.status === 'cooler');
      if (coolerEntry) {
        const startTime = parseInt(coolerEntry.startTime?.split(':')[0] || '18');
        const timing = startTime < 17 ? 'during the day' : 'tonight';
        coolerWorkers.push({ name: firstName, timing });
      }
    }
    
    // Check for abroad
    if (statuses.includes('abroad')) {
      abroadWorkers.push(firstName);
    }
    
    // Check for traveling
    if (statuses.includes('traveling')) {
      travelingWorkers.push(firstName);
    }

    // Check for meetings
    const meetingEntries = entries.filter(e => e.status === 'meeting');
    if (meetingEntries.length > 0) {
      const timings: string[] = [];
      meetingEntries.forEach(meeting => {
        const startTime = parseInt(meeting.startTime?.split(':')[0] || '12');
        if (startTime < 12) {
          timings.push('morning');
        } else if (startTime < 17) {
          timings.push('afternoon');
        } else {
          timings.push('evening');
        }
      });
      
      meetingWorkers.push({
        name: firstName,
        count: meetingEntries.length,
        timing: [...new Set(timings)] // Remove duplicates
      });
    }
  });

  // Full day workers
  if (fullDayWorkers.length > 0) {
    parts.push(`${formatNameList(fullDayWorkers)} ${fullDayWorkers.length === 1 ? 'is' : 'are'} working all day.`);
  }

  // Morning workers
  if (morningWorkers.length > 0) {
    parts.push(`${formatNameList(morningWorkers)} ${morningWorkers.length === 1 ? 'will be' : 'will be'} in this morning.`);
  }
  
  // Afternoon workers with meeting context
  if (afternoonWorkers.length > 0) {
    const afternoonWithMeetings = afternoonWorkers.map(name => {
      const meetingInfo = meetingWorkers.find(m => m.name === name);
      if (meetingInfo) {
        if (meetingInfo.count >= 3) {
          return `${name} (who has a lot of meetings today and may be unavailable for internal discussions)`;
        } else if (meetingInfo.timing.includes('afternoon')) {
          return `${name} (who also has a meeting)`;
        } else {
          return name;
        }
      }
      return name;
    });
    
    if (afternoonWithMeetings.some(name => name.includes('meeting'))) {
      // Custom formatting for meeting context
      parts.push(`${afternoonWithMeetings.join(' and ')} will be in this afternoon.`);
    } else {
      parts.push(`${formatNameList(afternoonWorkers)} ${afternoonWorkers.length === 1 ? 'will be' : 'will be'} in this afternoon.`);
    }
  }

  // Morning meeting workers (not already mentioned)
  const morningMeetingOnly = meetingWorkers.filter(m => 
    m.timing.includes('morning') && 
    !morningWorkers.includes(m.name) && 
    !fullDayWorkers.includes(m.name)
  );
  
  if (morningMeetingOnly.length > 0) {
    morningMeetingOnly.forEach(m => {
      if (m.count >= 3) {
        parts.push(`${m.name} has a lot of meetings this morning and may be unavailable for internal discussions.`);
      } else {
        parts.push(`${m.name} has ${m.count === 1 ? 'a meeting' : 'meetings'} this morning.`);
      }
    });
  }

  // Event workers
  if (eventWorkers.length > 0) {
    parts.push(`${formatNameList(eventWorkers)} ${eventWorkers.length === 1 ? 'is' : 'are'} working at an event.`);
  }

  // Cooler workers with timing
  if (coolerWorkers.length > 0) {
    // Group by timing
    const dayTimeCooler = coolerWorkers.filter(w => w.timing === 'during the day').map(w => w.name);
    const nightTimeCooler = coolerWorkers.filter(w => w.timing === 'tonight').map(w => w.name);
    
    if (dayTimeCooler.length > 0) {
      parts.push(`${formatNameList(dayTimeCooler)} ${dayTimeCooler.length === 1 ? 'is' : 'are'} working at the cooler during the day.`);
    }
    
    if (nightTimeCooler.length > 0) {
      parts.push(`${formatNameList(nightTimeCooler)} ${nightTimeCooler.length === 1 ? 'is' : 'are'} working at the cooler tonight.`);
    }
  } else {
    // Only mention if there are other workers
    if (workingCount > 0) {
      parts.push(`No one is working at the cooler today.`);
    }
  }

  // Abroad workers
  if (abroadWorkers.length > 0) {
    parts.push(`${formatNameList(abroadWorkers)} ${abroadWorkers.length === 1 ? 'is' : 'are'} working abroad.`);
  }

  // Traveling workers
  if (travelingWorkers.length > 0) {
    parts.push(`${formatNameList(travelingWorkers)} ${travelingWorkers.length === 1 ? 'is' : 'are'} traveling.`);
  }

  // Performances for today only
  if (performances.length > 0) {
    // Filter performances to only today and remove duplicates
    const todaysPerformances = performances.filter(perf => {
      const perfDate = new Date(perf.startTime);
      return perfDate.toDateString() === targetDate.toDateString();
    });

    // Remove duplicates by band name
    const uniquePerformances = todaysPerformances.reduce((unique: any[], perf: any) => {
      const bandName = perf.simpleBandName || 'Unknown Artist';
      if (!unique.find((p: any) => (p.simpleBandName || 'Unknown Artist') === bandName)) {
        unique.push(perf);
      }
      return unique;
    }, [] as any[]);

    if (uniquePerformances.length > 0) {
      if (uniquePerformances.length === 1) {
        const perf = uniquePerformances[0];
        const bandName = perf.simpleBandName || 'Unknown Artist';
        const staffOnPerf = perf.staff || [];
        
        if (staffOnPerf.length > 0) {
          const perfStaffNames = staffOnPerf
            .filter((s: any) => s.staffId && s.staffId.name)
            .map((s: any) => s.staffId.name.split(' ')[0]);
          
          if (perfStaffNames.length > 0) {
            const roleInfo = staffOnPerf
              .filter((s: any) => s.assignedRole)
              .map((s: any) => s.assignedRole)
              .join(', ');
            
            parts.push(`There's a performance by ${bandName} tonight with ${formatNameList(perfStaffNames)} working on it${roleInfo ? ` (${roleInfo})` : ''}.`);
          } else {
            parts.push(`There's a performance by ${bandName} tonight.`);
          }
        } else {
          parts.push(`There's a performance by ${bandName} tonight.`);
        }
      } else {
        // Multiple performances
        const bandNames = uniquePerformances
          .map((p: any) => p.simpleBandName || 'Unknown Artist')
          .filter((name: string) => name !== 'Unknown Artist');
        
        if (bandNames.length > 0) {
          parts.push(`There are ${uniquePerformances.length} performances tonight featuring ${formatNameList(bandNames)}.`);
        } else {
          parts.push(`There are ${uniquePerformances.length} performances tonight.`);
        }
      }
    }
  }

  // Time in lieu with timing context
  const tilWorkers: { name: string; timing: string[] }[] = [];
  entriesByPerson.forEach((entries, personId) => {
    const person = personnel.find(p => p._id === personId);
    if (!person) return;
    
    const firstName = person.name.split(' ')[0];
    const tilEntries = entries.filter(e => e.status === 'time-lieu');
    
    if (tilEntries.length > 0) {
      const timings: string[] = [];
      tilEntries.forEach(entry => {
        const startHour = parseInt(entry.startTime?.split(':')[0] || '10');
        const endHour = parseInt(entry.endTime?.split(':')[0] || '18');
        
        if (startHour <= 10 && endHour >= 17) {
          timings.push('for the day');
        } else if (startHour < 13) {
          timings.push('in the morning');
        } else if (startHour < 17) {
          timings.push('in the afternoon');
        } else {
          timings.push('in the evening');
        }
      });
      
      tilWorkers.push({ name: firstName, timing: [...new Set(timings)] });
    }
  });

  if (tilWorkers.length > 0) {
    tilWorkers.forEach(worker => {
      const timingText = worker.timing.join(' and ');
      parts.push(`${worker.name} is on time in lieu ${timingText}.`);
    });
  }

  // Other leave types
  const otherLeaveStatuses = ['annual-leave', 'sick-leave'];
  otherLeaveStatuses.forEach(status => {
    const onLeave: string[] = [];
    entriesByPerson.forEach((entries, personId) => {
      const person = personnel.find(p => p._id === personId);
      if (person && entries.some(e => e.status === status)) {
        onLeave.push(person.name.split(' ')[0]);
      }
    });
    
    if (onLeave.length > 0) {
      const statusLabel = getRotaOptionById(status)?.label || status;
      parts.push(`${formatNameList(onLeave)} ${onLeave.length === 1 ? 'is' : 'are'} on ${statusLabel.toLowerCase()}.`);
    }
  });

  // Unavailable staff (only 'unavailable' status)
  const unavailableStaff: string[] = [];
  console.log('Debug - Checking for unavailable staff...');
  todaysEntries.forEach(entry => {
    console.log('Debug - Entry:', entry.status, entry.personnelId);
    if (entry.status === 'unavailable') {
      if (!entry.personnelId) return;
      const person = personnel.find(p => p._id === (typeof entry.personnelId === 'string' ? entry.personnelId : (entry.personnelId as Personnel)._id));
      console.log('Debug - Found unavailable person:', person);
      if (person) {
        const firstName = person.name.split(' ')[0];
        if (!unavailableStaff.includes(firstName)) {
          unavailableStaff.push(firstName);
        }
      }
    }
  });

  console.log('Debug - Unavailable staff found:', unavailableStaff);

  if (unavailableStaff.length > 0) {
    parts.push(`${formatNameList(unavailableStaff)} ${unavailableStaff.length === 1 ? 'is' : 'are'} unavailable today.`);
  }

  return parts.join(' ');
};

const RotaWidget: React.FC<RotaWidgetProps> = ({ 
  initialDate = new Date() 
}) => {
  const [currentDate, setCurrentDate] = useState(initialDate);
  const [rotaEntries, setRotaEntries] = useState<RotaEntry[]>([]);
  const [personnel, setPersonnel] = useState<Personnel[]>([]);
  const [performances, setPerformances] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Format date as YYYY-MM-DD for the API
        const dateStr = currentDate.toISOString().split('T')[0];
        
        // Use the correct API endpoints from your backend
        const [rotaResponse, personnelResponse] = await Promise.all([
          axiosInstance.get(`/api/rota?startDate=${dateStr}&endDate=${dateStr}`),
          axiosInstance.get('/api/rota/personnel')
        ]);
        
        // Also fetch performances for the day
        let performances = [];
        try {
          const performanceResponse = await axiosInstance.get(`/api/performances?date=${dateStr}`);
          performances = performanceResponse.data || [];
        } catch (perfErr) {
          console.log('No performances endpoint or no performances today');
        }
        
        console.log('Rota data:', rotaResponse.data);
        console.log('Personnel data:', personnelResponse.data);
        console.log('Performances:', performances);
        
        setRotaEntries(rotaResponse.data || []);
        setPersonnel(personnelResponse.data || []);
        setPerformances(performances);
      } catch (err: any) {
        console.error('Error fetching rota data:', err);
        setError(err.response?.data?.message || 'Failed to fetch rota data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentDate.toISOString().split('T')[0]]); // Only re-run when the date string changes

  // Navigation functions
  const goToPreviousDay = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() - 1);
    setCurrentDate(newDate);
  };

  const goToNextDay = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + 1);
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Format date for display
  const formatDisplayDate = (date: Date): string => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === tomorrow.toDateString()) {
      return 'Tomorrow';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-US', { 
        weekday: 'long',
        month: 'short', 
        day: 'numeric' 
      });
    }
  };

  if (loading) {
    return (
      <Card sx={{ minWidth: 275, mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} sx={{ mr: 2 }} />
            <Typography>Loading today's schedule...</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card sx={{ minWidth: 275, mb: 2 }}>
        <CardContent>
          <Typography color="error">{error}</Typography>
        </CardContent>
      </Card>
    );
  }

  const summary = generateConversationalSummary(rotaEntries, personnel, performances, currentDate);

  return (
    <Card sx={{ minWidth: 275, mb: 2 }}>
      <CardContent>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between', 
          mb: 2,
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 2, sm: 0 }
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <PeopleIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6" component="h2">
              {formatDisplayDate(currentDate)}'s Schedule
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton 
              onClick={goToPreviousDay} 
              size="small"
              sx={{ color: 'primary.main' }}
            >
              <ChevronLeft />
            </IconButton>
            
            <Typography 
              variant="body2" 
              onClick={goToToday}
              sx={{ 
                cursor: 'pointer', 
                color: 'primary.main',
                fontWeight: 'medium',
                minWidth: { xs: '50px', sm: '60px' },
                textAlign: 'center',
                fontSize: { xs: '0.875rem', sm: '0.875rem' },
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
              Today
            </Typography>
            
            <IconButton 
              onClick={goToNextDay} 
              size="small"
              sx={{ color: 'primary.main' }}
            >
              <ChevronRight />
            </IconButton>
          </Box>
        </Box>
        
        <Typography 
          variant="body1" 
          color="text.primary"
          sx={{
            fontSize: { xs: '0.875rem', sm: '1rem' },
            lineHeight: { xs: 1.4, sm: 1.5 }
          }}
        >
          {summary}
        </Typography>
      </CardContent>
    </Card>
  );
};

export default RotaWidget;